class SupplyState$Off {
    constructor(reason) {
        this.reason = reason;
    }
    get isOff() {
        return true;
    }
    off(_supply, _reason) {
        // Already off.
    }
    whenOff(_supply, callback) {
        callback(this.reason);
    }
}
const SupplyState$done = ( /*#__PURE__*/new SupplyState$Off(void 0));
function SupplyState$off(reason) {
    return reason === undefined ? SupplyState$done : new SupplyState$Off(reason);
}

const SupplyState__symbol = ( /*#__PURE__*/Symbol('SupplyState'));

let Supply$unexpectedAbort = Supply$unexpectedAbort$byDefault;
function Supply$unexpectedAbort$handle(handler = Supply$unexpectedAbort$byDefault) {
    Supply$unexpectedAbort = handler;
}
function Supply$unexpectedAbort$byDefault(reason) {
    console.error('Supply aborted unexpectedly.', reason);
}

let Supply$off = 0;
let Supply$off$unexpected$reasons;
class SupplyState$On {
    get isOff() {
        return false;
    }
    off(supply, reason) {
        supply[SupplyState__symbol] = SupplyState$off(reason);
        if (Supply$off) {
            this.cb(reason);
        }
        else {
            Supply$off = 1;
            try {
                this.cb(reason);
            }
            finally {
                Supply$off = 0;
                Supply$off$unexpected$report();
            }
        }
    }
}
function Supply$off$unexpected(reason) {
    if (reason !== undefined) {
        if (!Supply$off$unexpected$reasons) {
            Supply$off$unexpected$reasons = new Set();
        }
        Supply$off$unexpected$reasons.add(reason);
    }
}
function Supply$off$unexpected$report() {
    const reasons = Supply$off$unexpected$reasons;
    if (reasons) {
        Supply$off$unexpected$reasons = undefined;
        for (const reason of reasons) {
            Supply$unexpectedAbort(reason);
        }
    }
}

class SupplyState$CbN extends SupplyState$On {
    constructor(cbs) {
        super();
        this.cbs = cbs;
    }
    whenOff(_supply, callback) {
        this.cbs.push(callback);
    }
    cb(reason) {
        for (const cb of this.cbs) {
            cb(reason);
        }
    }
}

class SupplyState$Cb1 extends SupplyState$On {
    constructor(cb) {
        super();
        this.cb = cb;
    }
    whenOff(supply, callback) {
        supply[SupplyState__symbol] = new SupplyState$CbN([this.cb, callback]);
    }
}

class SupplyState$Cb0 extends SupplyState$On {
    whenOff(supply, callback) {
        supply[SupplyState__symbol] = new SupplyState$Cb1(callback);
    }
    cb(reason) {
        Supply$off$unexpected(reason);
    }
}
const SupplyState$cb0 = ( /*#__PURE__*/new SupplyState$Cb0);

/**
 * Supply handle.
 *
 * Represents a supply of something.
 *
 * The supply can be {@link off cut off}, and can {@link whenOff inform} on cutting off.
 */
class Supply {
    /**
     * Constructs new supply instance.
     *
     * @param off - A function to call when the supply is {@link Supply.off cut off}. Accepts optional cut off reason
     * as its only parameter. No-op by default.
     */
    constructor(off) {
        this[SupplyState__symbol] = off ? new SupplyState$Cb1(off) : SupplyState$cb0;
    }
    /**
     * Assigns unexpected abort handler.
     *
     * When a supply {@link off aborted}, and there is no {@link whenOff cut off callback} registered, the given handler
     * will be called with the abort reason.
     *
     * By default, the unexpected abort reason will be logged to console.
     *
     * @param handler - A handler to call on unexpected abort, or `undefined` to reset to default one.
     */
    static onUnexpectedAbort(handler) {
        Supply$unexpectedAbort$handle(handler);
    }
    /**
     * `this` supply instance.
     */
    get supply() {
        return this;
    }
    /**
     * Whether this supply is {@link off cut off} already.
     *
     * `true` means nothing would be supplied any more.
     */
    get isOff() {
        return this[SupplyState__symbol].isOff;
    }
    /**
     * Cuts off this supply.
     *
     * After this method call nothing would be supplied any more.
     *
     * Calling this method for the second time has no effect.
     *
     * @param reason - An optional reason why the supply is cut off. It will be reported to {@link whenOff} callbacks.
     * By convenience, an absent reason means the supply is done successfully.
     *
     * @returns The cut off supply instance.
     */
    off(reason) {
        this[SupplyState__symbol].off(this, reason);
        return this;
    }
    /**
     * Registers a callback function that will be called as soon as this supply is {@link off cut off}. This callback
     * will be called immediately if {@link isOff} is `true`.
     *
     * @param callback - A callback function accepting optional cut off reason as its only parameter.
     * By convenience an `undefined` reason means the supply is done successfully.
     *
     * @returns `this` instance.
     */
    whenOff(callback) {
        this[SupplyState__symbol].whenOff(this, callback);
        return this;
    }
    /**
     * Builds a promise that will be resolved once this supply is {@link off done}.
     *
     * @returns A promise that will be successfully resolved once this supply is cut off without a reason, or rejected
     * once this supply is cut off with any reason except `undefined`.
     */
    whenDone() {
        return new Promise((resolve, reject) => this.whenOff(reason => reason === undefined ? resolve() : reject(reason)));
    }
    /**
     * Makes another supply depend on this one.
     *
     * Once this supply is {@link off cut off}, `another` one will be cut off with the same reason.
     *
     * Calling this method has the same effect as calling `another.supply.needs(this)`.
     *
     * @param another - A supply peer to make dependent on this one.
     *
     * @returns `this` instance.
     */
    cuts(another) {
        another.supply.needs(this);
        return this;
    }
    /**
     * Creates derived supply depending on this one.
     *
     * If derived supply peer specified, makes it depend on this one.
     *
     * In contrast to {@link cuts} method, this one returns derived supply.
     *
     * @param derived - Optional derived supply peer to make dependent on this one.
     *
     * @returns Derived supply.
     */
    derive(derived) {
        return (derived ? derived.supply : new Supply()).needs(this);
    }
    /**
     * Makes this supply depend on another one.
     *
     * Once `another` supply is {@link off cut off}, this one will be cut off with the same reason.
     *
     * @param another - A supply peer to make this one depend on.
     *
     * @returns `this` instance.
     */
    needs(another) {
        another.supply.whenOff(reason => this.off(reason));
        return this;
    }
    /**
     * Creates required supply this one depends on.
     *
     * If required supply peer specified, makes this one depend on it.
     *
     * In contrast to {@link needs} method, this one returns required supply.
     *
     * @param required - Optional required supply peer to make this one depend on.
     *
     * @returns Required supply.
     */
    require(required) {
        return (required ? required.supply : new Supply()).cuts(this);
    }
    /**
     * Makes this and another supply depend on each other.
     *
     * Calling this method is the same as calling `.needs(another).cuts(another)`.
     *
     * @param another - A supply peer to make this one to mutually depend on.
     *
     * @returns `this` instance.
     */
    as(another) {
        return this.needs(another).cuts(another);
    }
}

class AlwaysSupply extends Supply {
    get isOff() {
        return false;
    }
    off(_reason) {
        return this;
    }
    whenOff(_callback) {
        return this;
    }
    cuts(_another) {
        return this;
    }
    needs(_another) {
        return this;
    }
}
const alwaysSupply$ = ( /*#__PURE__*/new AlwaysSupply());
/**
 * Builds an always-supply instance.
 *
 * The {@link Supply.off} method of the returned supply does nothing.
 *
 * @returns A supply instance that can not be cut off.
 */
function alwaysSupply() {
    return alwaysSupply$;
}
/**
 * Checks whether the given supply is an {@link AlwaysSupply | always-supply} instance.
 *
 * @param supply - A supply to check.
 *
 * @returns `true` is the given `supply` can not be cut off, or `false` otherwise.
 */
function isAlwaysSupply(supply) {
    return supply === alwaysSupply$;
}

class NeverSupply extends Supply {
    get isOff() {
        return true;
    }
    off() {
        return this;
    }
    whenOff(callback) {
        callback();
        return this;
    }
    cuts(another) {
        another.supply.off();
        return this;
    }
    needs(_another) {
        return this;
    }
}
const neverSupply$ = ( /*#__PURE__*/new NeverSupply());
/**
 * Builds a never-supply instance.
 *
 * @returns A supply instance that is already cut off without any particular reason.
 */
function neverSupply() {
    return neverSupply$;
}

export { Supply, alwaysSupply, isAlwaysSupply, neverSupply };
//# sourceMappingURL=supply.js.map
